import { Fragment } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import { FS } from "@plannel/utils/font-util";

/**
 * 아이콘·그룹 헤더·검색어 하이라이트가 내장된 드롭다운 프리미티브.
 *
 * 팝업 셸과 행의 기본 룩(둥근 모서리·틴트 호버·13px)은 전역 테마(index.js의
 * MuiAutocomplete 오버라이드)가 담당하고, 이 컴포넌트는 콘텐츠 장식만 더한다:
 *  - 그룹 헤더: sticky + (groupIcons 제공 시) 모듈 아이콘
 *  - 옵션: (option.icon 제공 시) 아이콘 + 검색어 매칭 하이라이트(autosuggest-highlight)
 *
 * option 형태: { key, label, group?, icon? }
 *
 * @param {object[]} options  { key, label, group?, icon? } 배열
 * @param {Map} [groupIcons]  그룹 라벨 → 아이콘 엘리먼트 (그룹 헤더 장식)
 * @param {object} [textFieldProps]  renderInput의 TextField에 병합할 props
 * 나머지 props(sx, disabled, disableClearable 등)는 MUI Autocomplete로 전달된다.
 */
const AppAutocomplete = ({ options, value, onChange, placeholder, groupIcons, textFieldProps, ...rest }) => {
  const grouped = options?.some((option) => option.group != null);

  return (
    <Autocomplete size="small" options={options ?? []} value={value ?? null} onChange={onChange}
      groupBy={grouped ? (option) => option.group : undefined}
      getOptionLabel={(option) => option?.label ?? ""}
      isOptionEqualToValue={(option, val) => option.key === val.key}
      renderInput={(params) => <TextField {...params} placeholder={placeholder} {...textFieldProps} />}
      renderGroup={(params) => (
        <li key={params.key}>
          <Box sx={{
            position: "sticky", top: -4, zIndex: 1,
            display: "flex", alignItems: "center", gap: 0.75,
            px: 1.5, py: 0.75, bgcolor: "background.paper", borderBottom: 1, borderColor: "grey.100",
          }}>
            {groupIcons?.get(params.group) && (
              <Box sx={{ display: "flex", color: "primary.main", "& svg": { fontSize: "14px !important" } }}>
                {groupIcons.get(params.group)}
              </Box>
            )}
            <Typography sx={{ fontSize: FS.BODY, fontWeight: 700, color: "text.secondary", letterSpacing: 0.4 }}>{params.group}</Typography>
          </Box>
          <Box component="ul" sx={{ p: 0 }}>{params.children}</Box>
        </li>
      )}
      renderOption={(props, option, { inputValue }) => {
        const parts = parse(option.label, match(option.label, inputValue ?? "", { insideWords: true }));
        return (
          <Box component="li" {...props} key={option.key} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {option.icon && (
              <Box sx={{ display: "flex", color: "grey.500", flexShrink: 0, "& svg": { fontSize: "15px !important" } }}>
                {option.icon}
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {parts.map((part, idx) => part.highlight
                ? <Box key={idx} component="span" sx={{ color: "primary.main", fontWeight: 700 }}>{part.text}</Box>
                : <Fragment key={idx}>{part.text}</Fragment>)}
            </Box>
          </Box>
        );
      }}
      {...rest} />
  );
};

export default AppAutocomplete;
